import type { FormContext } from 'vee-validate'
import type { UseRegistrationData } from '~/store-v2/registration/useRegistration'
import { cpf } from '@solfacil/frontend-utils'
import * as z from 'zod'

export function getElectricityBillItemSchema(t: any) {
  const base = z.object({
    is_under_customer_name: z.boolean().default(true),
    electricity_bill_document: z.string({ message: t('form.required') }).min(1, { message: t('form.required') }),
    additional_document: z.string().optional().default(''),
  })

  return z.discriminatedUnion('is_under_customer_name', [
    base.extend({
      is_under_customer_name: z.literal(true),
      holder_cpf: z.string().optional(),
      employment_proof_document: z.string().optional(),
    }),
    base.extend({
      is_under_customer_name: z.literal(false),
      holder_cpf: z.string({ message: t('form.required') }).refine(v => cpf.validInput(v), { message: t('form.invalid_cpf') }),
      employment_proof_document: z.string({ message: t('form.required') }),
    }),
  ])
}

export function getSchema(t: any) {
  return z.object({
    electricity_bills: z.array(getElectricityBillItemSchema(t)).min(1).max(3),
  })
}

export type ElectricityBillItem = z.infer<ReturnType<typeof getElectricityBillItemSchema>>
export type FormSchema = z.infer<ReturnType<typeof getSchema>>
export type Form = FormContext<Partial<FormSchema>>
export type Field = keyof FormSchema | `electricity_bills[${number}]` | `electricity_bills[${number}].${keyof ElectricityBillItem}`

export type DocumentFields = 'electricity_bill_document' | 'additional_document' | 'employment_proof_document'

export function getInitialValues(): Partial<FormSchema> {
  return {
    electricity_bills: [{
      is_under_customer_name: true,
      electricity_bill_document: '',
      additional_document: '',
    }],
  }
}

export function getInitialBillItem(): Partial<ElectricityBillItem> {
  return {
    is_under_customer_name: true,
    electricity_bill_document: '',
    additional_document: '',
  }
}

export function extractDocumentEntries(form: Form) {
  const allDocuments: any[] = []

  form.values.electricity_bills?.forEach((bill, index) => {
    Object.entries(bill)
      .forEach(([key, value]) => {
        if (/document/i.test(key) && value) {
          allDocuments.push({
            type: key.replace('_document', ''),
            docstorage_id: value,
            electricity_bill_index: index,
          })
        }
      })
  })

  return allDocuments
}

export interface AnalysisError {
  field: string
  title: string
  description: string
  type: string
}

export function extractErrorsByField(registration?: UseRegistrationData): Record<string, AnalysisError> {
  const mappings: Record<string, string> = {
    holder_cpf: 'holder_cpf',
    electricity_bill: 'electricity_bill_document',
    additional: 'additional_document',
    employment_proof: 'employment_proof_document',
  } as const

  return (registration?.errors || [])
    .filter(error => !!error)
    .reduce((acc, error) => {
      const [_, category, id, field] = error.field.split('.')
      if (category === 'electricity_bills') {
        const index = registration?.electricity_bills?.findIndex(bill => bill.id === id)
        if (index !== undefined) {
          const mappedField = mappings[field]
          if (mappedField) {
            acc[`electricity_bills[${index}].${mappedField}`] = {
              field: `electricity_bills[${index}].${mappedField}`,
              title: error.title || '',
              description: error.description,
              type: error.type || 'warning',
            }
          }
        }
      }
      return acc
    }, {} as Record<string, AnalysisError>)
}
